import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FIELD_LABELS = {
  monthlySalary: 'Monthly Salary',
  spfValue: 'SPF Value',
  spfPercentage: 'SPF %',
  healthInsuranceValue: 'Health Insurance Value',
  healthInsuranceEnabled: 'Health Insurance',
  utilizationDefault: 'Utilization %',
  hourlySalary: 'Hourly Rate',
  dailySalary: 'Daily Rate',
  unitCost: 'Unit Cost',
  wastePercentage: 'Waste %',
  cost: 'License Cost',
  monthlyCost: 'Monthly Cost',
  // Non-numeric fields
  role: 'Role',
  specialization: 'Specialization',
  nationality: 'Nationality',
  itemName: 'Item Name',
  uom: 'Unit of Measure',
  softwareName: 'Software Name',
  licenseType: 'License Type',
};

const formatValue = (key, value) => {
  if (key === 'healthInsuranceEnabled') return value ? 'Enabled' : 'Disabled';
  if (key === 'nationality') {
    if (value === 'omani') return 'Omani';
    if (value === 'expat') return 'Expat';
    return String(value ?? '—');
  }
  if (key === 'role') {
    const roleMap = { senior: 'Senior Engineer', mid: 'Mid-Level Engineer', junior: 'Junior Engineer', lead: 'Lead Engineer', architect: 'Solution Architect' };
    return roleMap?.[value] || String(value ?? '—');
  }
  if (key === 'licenseType') {
    const typeMap = { monthly: 'Monthly Subscription', annual: 'Annual Subscription', perpetual: 'Perpetual License' };
    return typeMap?.[value] || String(value ?? '—');
  }
  if (typeof value === 'number') {
    if (key?.toLowerCase()?.includes('percentage') || key?.toLowerCase()?.includes('utilization')) {
      return `${value?.toFixed(2)}%`;
    }
    return `OMR ${value?.toFixed(3)}`;
  }
  return String(value ?? '—');
};

const ResourceRow = ({ change, isSelected, onToggle }) => {
  const diff = change?.changedFields || [];
  return (
    <div
      className={`border rounded-xl p-4 transition-all duration-200 cursor-pointer ${
        isSelected
          ? 'border-primary/60 bg-primary/5' :'border-border bg-card hover:border-border/80'
      }`}
      onClick={onToggle}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
            isSelected ? 'bg-primary border-primary' : 'border-border bg-background'
          }`}
        >
          {isSelected && <Icon name="Check" size={12} color="white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-foreground truncate">{change?.name}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
              change?.type === 'engineer' ? 'bg-blue-500/10 text-blue-600' :
              change?.type === 'outsource' ? 'bg-purple-500/10 text-purple-600' :
              change?.type === 'material'? 'bg-green-500/10 text-green-600' : 'bg-orange-500/10 text-orange-600'
            }`}>
              {change?.type === 'engineer' ? 'Engineer' :
               change?.type === 'outsource' ? 'Outsource' :
               change?.type === 'material' ? 'Material' : 'License'}
            </span>
          </div>
          <div className="space-y-2">
            {diff?.map((field) => (
              <div key={field?.key} className="grid grid-cols-3 gap-2 text-xs">
                <span className="text-muted-foreground font-medium">{FIELD_LABELS?.[field?.key] || field?.key}</span>
                <div className="flex items-center gap-1">
                  <span className="px-2 py-1 bg-error/10 text-error rounded font-mono line-through">
                    {formatValue(field?.key, field?.savedValue)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" className="flex-shrink-0" />
                  <span className="px-2 py-1 bg-success/10 text-success rounded font-mono font-semibold">
                    {formatValue(field?.key, field?.currentValue)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MasterDataSyncDialog = ({ isOpen, changes, onApplyAll, onApplySelected, onKeepOriginal, onMinimize }) => {
  const [selected, setSelected] = useState(() => new Set(changes?.map(c => c?.id) || []));

  // Block Escape key while dialog is open
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        e?.preventDefault();
        e?.stopPropagation();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleItem = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected?.size === changes?.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(changes?.map(c => c?.id)));
    }
  };

  const handleApplySelected = () => {
    const selectedChanges = changes?.filter(c => selected?.has(c?.id));
    onApplySelected?.(selectedChanges);
  };

  const allSelected = selected?.size === changes?.length;
  const someSelected = selected?.size > 0 && selected?.size < changes?.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-border flex-shrink-0">
          <div className="p-2 rounded-xl bg-warning/15">
            <Icon name="RefreshCw" size={20} color="var(--color-warning)" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground">Review Master Data Changes</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {changes?.length} resource{changes?.length !== 1 ? 's have' : ' has'} updated values in Master Data
            </p>
          </div>
          {onMinimize && (
            <button
              onClick={onMinimize}
              title="Minimize"
              className="ml-2 p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
            >
              <Icon name="X" size={16} color="var(--color-muted-foreground)" />
            </button>
          )}
        </div>

        {/* Column headers */}
        <div className="px-5 py-2 bg-muted/30 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 cursor-pointer transition-colors ${
                allSelected ? 'bg-primary border-primary' : someSelected ? 'bg-primary/40 border-primary' : 'border-border bg-background'
              }`}
              onClick={toggleAll}
            >
              {allSelected && <Icon name="Check" size={12} color="white" />}
              {someSelected && <div className="w-2 h-0.5 bg-white rounded" />}
            </div>
            <div className="flex-1 grid grid-cols-3 gap-2 text-xs font-medium text-muted-foreground">
              <span>Field</span>
              <span>Saved Value</span>
              <span>Current Master Data</span>
            </div>
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {changes?.map((change) => (
            <ResourceRow
              key={change?.id}
              change={change}
              isSelected={selected?.has(change?.id)}
              onToggle={() => toggleItem(change?.id)}
            />
          ))}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-border flex-shrink-0 bg-muted/20">
          <Button
            variant="ghost"
            iconName="X"
            iconPosition="left"
            onClick={onKeepOriginal}
            className="text-muted-foreground"
          >
            Keep Original
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              iconName="CheckSquare"
              iconPosition="left"
              onClick={handleApplySelected}
              disabled={selected?.size === 0}
            >
              Apply Selected ({selected?.size})
            </Button>
            <Button
              variant="default"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={onApplyAll}
            >
              Apply All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterDataSyncDialog;
